import { Theme, css } from '@emotion/react';

import { Spacing, Typography } from 'chatie-design-system';

import Avatar, { AvatarSize } from '@components/common/Avatar';
import SvgIcon from '@components/common/SvgIcon';
import Tag from '@components/common/Tag';

import FemaleIcon from '@public/icons/cheming/female_badge.svg';
import MaleIcon from '@public/icons/cheming/male_badge.svg';
import IcPlay from '@public/icons/ic_play.svg';

import { useGlobalContext } from '@contexts/global';
import { IChatCharacter, ITag } from '@model';
import { formatNumberMillion } from '@utils/chat';

const cardContainerCSS = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0 16px;
`;

const profileCSS = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${Spacing.xhsmall}px;
  margin-bottom: ${Spacing.small}px;

  ${Typography.body11};
  color: ${theme.var.gray600};

  > div {
    margin-bottom: ${Spacing.smallPlus}px;
  }

  h5 {
    color: ${theme.var.gray900};
    ${Typography.h5};
  }

  .character-avatar {
    position: relative;
    top: 0;
  }
  .play-cnt {
    display: flex;
    gap: 3px;
    position: absolute;
    top: 8px;
    left: 8px;
    color: ${theme.var.white};
    z-index: 1;
  }

  .play-cnt-icon {
    path {
      fill: ${theme.var.white};
    }
  }

  .character-type {
    color: ${theme.var.gray900};
    ${Typography.body12};
    margin-bottom: ${Spacing.xxsmall}px;
  }

  .gender-badge {
    margin: 10px 0;
  }
`;

const tagContainerCSS = css`
  display: flex;
  justify-content: center;
  position: relative;

  .tagList {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    height: '100%';
    overflow: hidden;
    gap: ${Spacing.xsmall}px;

    .icon-text {
      display: flex;
      height: auto;
      padding: ${Spacing.xhsmall}px ${Spacing.xsmall}px;
      margin: 0;
    }
  }

  .icon-btn {
    position: absolute;
    bottom: 6px;
    right: 35px;
  }
`;

interface CharacterProfileCardProps {
  character: IChatCharacter;
  handleClickTag?: (id: number, name: string) => void;
  type: 'character' | 'profile';
  gender?: 'male' | 'female' | '남자' | '여자';
  tagSet?: ITag[];
}

const CharacterProfileCard = ({
  character,
  handleClickTag,
  type,
  gender,
  tagSet,
}: CharacterProfileCardProps) => {
  const isCharacter = type === 'character';
  const characterGender = gender ?? character?.information?.gender;
  const characterTagSet = tagSet;
  const { isCheming } = useGlobalContext();

  return (
    <div css={cardContainerCSS}>
      <div css={profileCSS}>
        <div className="character-avatar">
          {isCharacter && !isCheming && (
            <div className={'play-cnt'}>
              <SvgIcon icon={IcPlay} className={'play-cnt-icon'} />

              {formatNumberMillion(character?.stat?.message_count)}
            </div>
          )}
          <Avatar
            picture={character?.profile_image}
            size={AvatarSize.xxxlarge}
          />
        </div>
        <p className="character-type">
          {type === 'profile' ? '나의 페르소나' : '캐릭터'}
        </p>
        <h5>{character?.name}</h5>

        {characterGender === '남자' || characterGender === 'male' ? (
          <SvgIcon icon={MaleIcon} className={'gender-badge'} />
        ) : (
          <SvgIcon icon={FemaleIcon} className={'gender-badge'} />
        )}

        <p>{character.greeting}</p>
      </div>

      <div css={tagContainerCSS}>
        <div className="tagList">
          {characterTagSet?.map((item, index) => (
            <Tag
              {...item}
              key={`tag_${index}`}
              onClick={() => handleClickTag?.(item.id, item.name)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CharacterProfileCard;
